import type {
  Credential,
  LocalizedText,
} from "../../api/CertRoadmap/types";
import catalogData from "../../data/certificates_catalog.json";

export type DifficultyScore = 1 | 2 | 3 | 4 | 5;

export type CatalogCategory = {
  id: string;
  labelAr: string;
  labelEn: string;
};

type CatalogCertificate = {
  id: string;
  code: string;
  nameEn: string;
  nameAr: string;
  primaryCategoryId: string;
  categoryIds: string[];
  difficultyScore: DifficultyScore;
  editorialOrder: number;
};

type DifficultyDefinition = {
  key: string;
  labelAr: string;
  labelEn: string;
};

type CertificateCatalog = {
  schemaVersion: number;
  difficultyScale: Record<`${DifficultyScore}`, DifficultyDefinition>;
  categories: CatalogCategory[];
  certificates: CatalogCertificate[];
};

export type LibraryCredential = {
  id: string;
  code: string;
  logo: string;
  name: LocalizedText;
  summary: LocalizedText;
  primaryCategoryId: string;
  categoryIds: string[];
  difficultyScore: DifficultyScore;
  editorialOrder: number;
  videoSegmentId: string;
};

export type CertificateSort =
  | "recommended"
  | "az"
  | "za"
  | "difficulty-asc"
  | "difficulty-desc";

export const sortOptions: {
  value: CertificateSort;
  labelEn: string;
  labelAr: string;
}[] = [
  {
    value: "recommended",
    labelEn: "Recommended Path",
    labelAr: "الترتيب الأصلي",
  },
  { value: "az", labelEn: "A → Z", labelAr: "أبجديًا تصاعدي" },
  { value: "za", labelEn: "Z → A", labelAr: "أبجديًا تنازلي" },
  {
    value: "difficulty-asc",
    labelEn: "Difficulty: Low to High",
    labelAr: "الأسهل أولًا",
  },
  {
    value: "difficulty-desc",
    labelEn: "Difficulty: High to Low",
    labelAr: "الأصعب أولًا",
  },
];

const catalog = catalogData as CertificateCatalog;

export const catalogCategories = catalog.categories;
export const catalogCertificates = catalog.certificates;
export const difficultyScale = catalog.difficultyScale;

export const categoryById = new Map(
  catalogCategories.map((category) => [category.id, category])
);

const normalizeCode = (code: string) => code.trim().toLocaleLowerCase("en");

const videoSegmentIdFor = (certificate: CatalogCertificate) =>
  certificate.id === "mba-dba" ? "professional_mba_dba" : certificate.id;

export const mergeCatalogCredentials = (
  credentials: Credential[]
): LibraryCredential[] => {
  const credentialsByCode = new Map(
    credentials.map((credential) => [normalizeCode(credential.code), credential])
  );
  const credentialsById = new Map(
    credentials.map((credential) => [credential.id, credential])
  );

  return catalogCertificates.map((certificate) => {
    const apiCredential =
      credentialsByCode.get(normalizeCode(certificate.code)) ??
      credentialsById.get(certificate.id) ??
      credentialsById.get(videoSegmentIdFor(certificate));

    return {
      id: certificate.id,
      code: certificate.code,
      logo: apiCredential?.logo ?? "",
      name: { en: certificate.nameEn, ar: certificate.nameAr },
      summary: apiCredential?.summary ?? { en: "", ar: "" },
      primaryCategoryId: certificate.primaryCategoryId,
      categoryIds: certificate.categoryIds,
      difficultyScore: certificate.difficultyScore,
      editorialOrder: certificate.editorialOrder,
      videoSegmentId: apiCredential?.id ?? videoSegmentIdFor(certificate),
    };
  });
};

const compareCode = (first: LibraryCredential, second: LibraryCredential) =>
  first.code.localeCompare(second.code, "en", { sensitivity: "base" });

const sorters: Record<
  CertificateSort,
  (first: LibraryCredential, second: LibraryCredential) => number
> = {
  recommended: (first, second) =>
    first.editorialOrder - second.editorialOrder,
  az: compareCode,
  za: (first, second) => compareCode(second, first),
  "difficulty-asc": (first, second) =>
    first.difficultyScore - second.difficultyScore ||
    compareCode(first, second),
  "difficulty-desc": (first, second) =>
    second.difficultyScore - first.difficultyScore ||
    compareCode(first, second),
};

export const filterAndSortCredentials = (
  credentials: LibraryCredential[],
  selectedCategoryIds: string[],
  selectedDifficultyScores: DifficultyScore[],
  selectedSort: CertificateSort
) =>
  credentials
    .filter(
      (credential) =>
        selectedCategoryIds.length === 0 ||
        credential.categoryIds.some((categoryId) =>
          selectedCategoryIds.includes(categoryId)
        )
    )
    .filter(
      (credential) =>
        selectedDifficultyScores.length === 0 ||
        selectedDifficultyScores.includes(credential.difficultyScore)
    )
    .sort(sorters[selectedSort]);

export const isCertificateSort = (
  value: string | null
): value is CertificateSort =>
  sortOptions.some((option) => option.value === value);

export const categoryColors: Record<string, string> = {
  "accounting-reporting": "#2563eb",
  "management-strategy": "#7c3aed",
  "modeling-fpa": "#ea580c",
  "data-technology": "#0891b2",
  "treasury-risk-investment": "#059669",
  "audit-governance-fraud": "#e11d48",
  "public-accounting-practice": "#b45309",
  taxation: "#dc2626",
  "project-management": "#4f46e5",
  "executive-leadership": "#9333ea",
};
